import styled from '@emotion/styled'
import { Site } from '@pubstack/common/src/site'
import { Stack } from '@pubstack/common/src/stack'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toBulkUpdate, STACKS_QUERY_KEYS, useBulkUpdateStacks } from '~/api/stacks.api'
import Button from '~/components/Button'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { AbDeploySetRatiosTable } from './AbDeploySetRatiosTable'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Info = styled.p`
  font-style: italic;
`

// Tracking labels
const MODAL_TRACKING_LABEL = 'pbs-sites-stacks-deploy-modal'
const CLOSE_TRACKING_LABEL = `${MODAL_TRACKING_LABEL}-close`
const DEPLOY_TRACKING_LABEL = `${MODAL_TRACKING_LABEL}-apply`
const CONNECT_TRACKING_LABEL = `${MODAL_TRACKING_LABEL}-connect-navigate`

type Props = {
  site: Site
  stack: Stack
  concurrentStack: Stack | undefined
}

export const AbDeployModal: FunctionComponent<Props> = ({ site, stack, concurrentStack }) => {
  const liveStack = concurrentStack?.status === 'live' ? concurrentStack : undefined
  const contextHasChanged = liveStack !== undefined && stack.configuration.contextId !== liveStack.configuration.contextId
  const isFirstDeployment = stack.version === 1

  const [ratio, setRatio] = useState<number>(1)

  const { mutateAsync: bulkUpdateStacks, isPending } = useBulkUpdateStacks({
    successToast: (
      <>
        <strong>
          {stack.name} v{stack.version}
        </strong>{' '}
        stack is <strong>Live</strong>
      </>
    ),
    errorToast: 'An error occurred while trying to deploy the stack.',
  })

  const queryClient = useQueryClient()
  const { close } = useGlobalModal()
  const navigate = useNavigate()

  const handleDeploy = useCallback(async () => {
    const effectiveRatio = liveStack && !contextHasChanged ? ratio : 1
    const toUpdate = [toBulkUpdate({ ...stack, status: 'live', ratio: effectiveRatio })]
    if (liveStack) {
      const liveRatio = +(1 - effectiveRatio).toFixed(2)
      toUpdate.push(toBulkUpdate({ ...liveStack, status: liveRatio === 0 ? 'archived' : 'live', ratio: liveRatio }))
    }
    await bulkUpdateStacks(toUpdate)
    queryClient.invalidateQueries({ queryKey: [...STACKS_QUERY_KEYS, { siteId: stack.siteId }] })
    close()
  }, [bulkUpdateStacks, stack, liveStack, contextHasChanged, ratio, queryClient, close])

  const handleConnectToPubstack = useCallback(() => {
    navigate(`/${stack.type === 'adm' ? 'adm' : 'pbs'}/sites/${site.id}/connect-to-pubstack`)
    close()
  }, [navigate, stack.type, site.id, close])

  return (
    <Modal.Content>
      <Modal.Title>Deploy to production</Modal.Title>
      <Modal.Body style={{ maxWidth: '544px' }}>
        <Wrapper>
          <p>
            You are about to deploy the stack{' '}
            <strong>
              {stack.name} v{stack.version}.
            </strong>
          </p>
          {isFirstDeployment && <p>Before deploying it, make sure to connect the site to Pubstack with the right tag.</p>}
          {liveStack && !contextHasChanged && <AbDeploySetRatiosTable stack={stack} liveStack={liveStack} ratio={ratio} onChange={setRatio} />}
          {contextHasChanged && <Info>As this version uses a different context than the current one, it must be fully deployed and can't be tested on the same stack.</Info>}
          {liveStack && contextHasChanged && (
            <Info>
              The following stack will be archived: {liveStack.name} v{liveStack.version}.
            </Info>
          )}
        </Wrapper>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="tertiary" onClick={close} trackingLabel={CLOSE_TRACKING_LABEL}>
          Cancel
        </Button>
        {isFirstDeployment && (
          <Button variant="secondary" onClick={handleConnectToPubstack} trackingLabel={CONNECT_TRACKING_LABEL}>
            Connect to Pubstack
          </Button>
        )}
        <Button variant="primary" onClick={handleDeploy} trackingLabel={DEPLOY_TRACKING_LABEL} disabled={isPending} iconSpin={isPending} iconName={isPending ? 'pubstack' : 'send'}>
          Deploy
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
